import { HomePeopleRawNormDataItem } from "@api/useHomePeopleRawNormData";
import { useMemo } from "react";
import { useKeywordNameTableCell } from "@vms/TableCells/useKeywordNameTableCell";
import { useMacStageTableCell } from "@vms/TableCells/useMacStageTableCell";
import { TableColumn } from "@components/Table/TableColumns";
import { useDisplayMetricContext, useHomeContext } from "@pages/Home/HomeContext";
import { usePeopleViewContext } from "@pages/Home/components/PeopleView/PeopleViewContext";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import { ALL_RESPONDENT_TYPES } from "@models/RespondentType";
import { useGetRespondentRawTableCell } from "@vms/TableCells/useGetRespondentRawTableCell";
import { COMMON_NUM_COL_WIDTH_PX, HomeShow } from "@core/constants";
import { getRem } from "@datassential/platform-ui-lib";
import { useGetRespondentNormTableCell } from "@vms/TableCells/useGetRespondentNormTableCell";
import { useGetEmptyTableCells } from "@vms/TableCells/useGetEmptyTableCells";

export type PeopleViewRawNormTableConfigProps = {
    data: HomePeopleRawNormDataItem[];
}

export function usePeopleViewRawNormTableConfig({
    data,
}: PeopleViewRawNormTableConfigProps) {
    const { show } = usePeopleViewContext();
    const { displayMetricId } = useDisplayMetricContext();
    const { search } = useHomeContext();
    const { data: { respondentTypes } } = useSelectAmongContext();
    const trimmedSearch = useMemo(() => search.trim(), [search]);
    const hideSorting = useMemo(() => trimmedSearch.length > 0, [trimmedSearch]);

    const selectedRespondentTypesIds = useMemo(
        () => ALL_RESPONDENT_TYPES.filter(i => respondentTypes.includes(i)),
        [respondentTypes]
    );

    const isBaseSizeView = useMemo(
        () => displayMetricId === null,
        [displayMetricId]
    );

    const nameColumn = useKeywordNameTableCell(data, trimmedSearch);
    const macStageColumn = useMacStageTableCell(data);
    const rawColumn = useGetRespondentRawTableCell(data);
    const normColumn = useGetRespondentNormTableCell(data);
    const getEmptyCells = useGetEmptyTableCells();

    const colWidth = useMemo(
        () => getRem(COMMON_NUM_COL_WIDTH_PX * 11 / selectedRespondentTypesIds.length),
        [selectedRespondentTypesIds.length]
    );

    const paddingCols = useMemo(
        () => {
            if (selectedRespondentTypesIds.length === 0) {
                return getEmptyCells(11);
            }

            return [];
        },
        [selectedRespondentTypesIds, getEmptyCells]
    );

    return useMemo(
        (): TableColumn[] => [
            {
                ...nameColumn,
                sortKey: hideSorting ? undefined : 'name',
                style: {
                    ...nameColumn.style,
                    cell: {
                        ...nameColumn.style?.cell,
                        flex: `1 0 ${getRem(200)}`,
                    }
                },
            },
            {
                ...macStageColumn,
                sortKey: hideSorting ? undefined : 'macStage',
            },
            ...selectedRespondentTypesIds.flatMap((id) => {
                const raw = rawColumn(id);
                const norm = normColumn(id);
                const base = rawColumn(id, 'base');

                return [
                    {
                        ...raw,
                        sortKey: hideSorting ? undefined : `column${id}`,
                        isHidden: show !== HomeShow.Raw || isBaseSizeView,
                        isHiddenForTextMode: true,
                        style: {
                            ...raw.style,
                            cell: {
                                ...raw.style?.cell,
                                flex: `0 0 ${colWidth}`,
                            },
                        },
                    },
                    {
                        ...norm,
                        sortKey: hideSorting ? undefined : `column${id}`,
                        isHidden: show !== HomeShow.Norm || isBaseSizeView,
                        isHiddenForTextMode: true,
                        style: {
                            ...norm.style,
                            cell: {
                                ...norm.style?.cell,
                                flex: `0 0 ${colWidth}`,
                            },
                        },
                    },
                    {
                        ...base,
                        sortKey: hideSorting ? undefined : `column${id}`,
                        isHidden: !isBaseSizeView,
                        isHiddenForTextMode: true,
                        style: {
                            ...base.style,
                            cell: {
                                ...base.style?.cell,
                                flex: `0 0 ${colWidth}`,
                            },
                        },
                    },
                ];
            }),
            {
                key: 'raw-copy',
                isHidden: isBaseSizeView,
                isHiddenForRender: true,
                head: '',
                headText: 'Raw',
                Value: () => null,
                subColumns: ALL_RESPONDENT_TYPES.map(i => rawColumn(i)),
            },
            {
                key: 'norm-copy',
                isHidden: isBaseSizeView,
                isHiddenForRender: true,
                head: '',
                headText: 'Norms',
                Value: () => null,
                subColumns: ALL_RESPONDENT_TYPES.map(i => normColumn(i)),
            },
            {
                key: 'base-copy',
                isHidden: !isBaseSizeView,
                isHiddenForRender: true,
                head: '',
                headText: 'Base',
                Value: () => null,
                subColumns: ALL_RESPONDENT_TYPES.map(i => rawColumn(i, 'base')),
            },
            ...paddingCols,
        ],
        [
            nameColumn,
            macStageColumn,
            rawColumn,
            normColumn,
            selectedRespondentTypesIds,
            hideSorting,
            show,
            isBaseSizeView,
            colWidth,
            paddingCols
        ]
    );
}